import React from 'react'
import { Tooltip, Icon, Typography, Box } from '@mui/material'
import { Rating } from '@mui/material'
import { withStyles } from '@mui/styles'

import { ratingInputStyles } from './rating-input.styles'

/**
 * @typedef {object} RatingInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {boolean} [readOnly=false] - Si es `true`, el rating se muestra en modo de solo lectura.
 * @property {boolean} [explicit=false] - Si es `true`, muestra el valor numérico junto a las estrellas en modo solo lectura.
 * @property {'small' | 'medium' | 'large'} [size='medium'] - Tamaño de las estrellas.
 * @property {string} [title] - Título que se muestra encima del rating.
 * @property {string} [tooltip] - Texto del tooltip de ayuda.
 * @property {number} [value] - El valor actual del rating (0-5).
 * @property {string} name - Nombre del campo para el evento onChange.
 * @property {function(object): void} onChange - Callback que se ejecuta cuando el rating cambia.
 * @property {number} [precision=1] - Precisión del rating (ej. 0.5 para medias estrellas).
 * @property {'small' | 'medium'} [fontSize] - Tamaño de la fuente para el título.
 */

/**
 * Componente de entrada de calificación por estrellas.
 * 
 * Proporciona un sistema de rating visual con:
 * - **Estrellas interactivas**: Selección de calificación de 0 a 5 estrellas
 * - **Precisión configurable**: Soporte para medias estrellas u otras fracciones
 * - **Modo solo lectura**: Visualización de ratings existentes
 * - **Valor explícito**: Opción para mostrar el número junto a las estrellas
 * - **Tooltip de ayuda**: Información contextual opcional
 * 
 * @component
 * @example
 * const MyReview = () => {
 *   const [rating, setRating] = useState(3.5);
 * 
 *   return (
 *     <RatingInput
 *       name="productRating"
 *       title="Calificación del Producto"
 *       value={rating}
 *       onChange={(e) => setRating(e.target.value)}
 *       precision={0.5}
 *       tooltip="Califica de 0 a 5 estrellas"
 *     />
 *   );
 * };
 * 
 * @param {RatingInputProps} props - Las props del componente RatingInput.
 */
export const RatingInput = React.memo(
  withStyles(ratingInputStyles)(
    ({ classes, readOnly = false, explicit = false, size = 'medium', ...props }) => {
      const value = props.value ? props.value : null
      const tooltip = (
        <span style={{ fontSize: 16, marginLeft: 4 }}>
          <Tooltip
            placement="top"
            title={
              <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                {props.tooltip}
              </Typography>
            }
          >
            <Icon fontSize="inherit">help_outline</Icon>
          </Tooltip>
        </span>
      )

      const typographyVariant = props.fontSize === 'small' ? '2' : '1'

      const handleChange = (_, value) => {
        props.onChange({ target: { name: props.name, value } })
      }

      return (
        <>
          {props.title ? (
            <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
              {props.title}
              {props.tooltip && tooltip}
            </Typography>
          ) : null}
          <Box
            display="flex"
            alignItems="center"
            className={readOnly ? classes.readMode : classes.editMode}
          >
            <Rating
              {...{
                name: props.name,
                value: value,
                readOnly,
                size: readOnly ? size : 'large',
                onChange: handleChange,
                precision: props.precision,
              }}
            />
            {readOnly && explicit ? (
              <Box ml={1}>
                <Typography size={readOnly ? size : 'large'} variant={`body${typographyVariant}`}>
                  {value ? value : ''}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </>
      )
    }
  )
)
